import { alpha, Box, Checkbox, Chip, Tooltip, Typography } from '@mui/material';
import * as React from 'react';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { DicomRoi } from '~/domain/dicom/dicomRoi';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { Modality } from '~/domain/dicom/modality';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { StructureSet } from '~/domain/patient/structureset';
import {
    ImageSeriesEdits,
    RoiEdits,
    StructureSetEdits,
} from '~/hooks/export/useExportEditing';
import ExportListItem from './ExportListItem';
import DoseExportDetails from './ExportListItem/DoseExportDetails';
import ImageExportDetails from './ExportListItem/ImageExportDetails';
import StructureSetExportDetails from './ExportListItem/StructureSetExportDetails';

type ExportableItemsListProps = {
    exportableImages: Image3D[];
    exportableStructureSets: StructureSet[];
    exportableDoses: DoseMatrix[];
    exportableRegistrations: RigidRegistration[];
    selectedImages: Image3D[];
    selectedStructureSets: StructureSet[];
    selectedDoses: DoseMatrix[];
    selectedRegistrations: RigidRegistration[];
    structureSelections: Record<string, DicomRoi[]>;
    defaultExpanded: string;
    handleSelectImage: (image: Image3D) => void;
    handleSelectStructureSet: (structureSet: StructureSet) => void;
    handleSelectStructure: (
        structureSetId: string,
        structure: DicomRoi
    ) => void;
    handleSelectDose: (dose: DoseMatrix) => void;
    handleSelectRegistration: (registration: RigidRegistration) => void;
    handleSelectAll: (selectAll: boolean) => void;
    imageEdits: Record<string, ImageSeriesEdits>;
    setImageSeriesDescription: (uid: string, description: string) => void;
    setImageSeriesNumber: (uid: string, seriesNumber: number) => void;
    ssEdits: Record<string, StructureSetEdits>;
    roiEdits: RoiEdits;
    setSsLabel: (uid: string, label: string) => void;
    setSsApproval: (uid: string, status: DICOMApprovalStatus) => void;
    setSsReviewer: (uid: string, name: string) => void;
    setRoiName: (ssUid: string, roiNumber: number, name: string) => void;
    setRoiType: (
        ssUid: string,
        roiNumber: number,
        type: RoiInterpretedType
    ) => void;
};

type FlatItem =
    | { type: 'image'; item: Image3D }
    | { type: 'structureset'; item: StructureSet; subtitle?: string }
    | { type: 'dose'; item: DoseMatrix };

const ExportDatasetModal = ({
    exportableImages,
    exportableStructureSets,
    exportableDoses,
    exportableRegistrations,
    selectedImages,
    selectedStructureSets,
    selectedDoses,
    selectedRegistrations,
    structureSelections,
    defaultExpanded,
    handleSelectImage,
    handleSelectStructureSet,
    handleSelectStructure,
    handleSelectDose,
    handleSelectRegistration,
    handleSelectAll,
    imageEdits,
    setImageSeriesDescription,
    setImageSeriesNumber,
    ssEdits,
    roiEdits,
    setSsLabel,
    setSsApproval,
    setSsReviewer,
    setRoiName,
    setRoiType,
}: ExportableItemsListProps) => {
    const [expanded, setExpanded] = React.useState<string>(defaultExpanded);

    const handleExpand =
        (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) =>
            setExpanded(isExpanded ? panel : '');

    const hasExportableItems =
        exportableImages.length > 0 ||
        exportableStructureSets.length > 0 ||
        exportableDoses.length > 0 ||
        exportableRegistrations.length > 0;

    const totalExportableCount =
        exportableImages.length +
        exportableStructureSets.length +
        exportableDoses.length +
        exportableRegistrations.length;

    const totalSelectedCount =
        selectedImages.length +
        selectedStructureSets.length +
        selectedDoses.length +
        selectedRegistrations.length;

    const allSelected =
        totalExportableCount > 0 && totalSelectedCount === totalExportableCount;
    const someSelected = totalSelectedCount > 0 && !allSelected;

    // Flat ordered list: each image immediately followed by its structure sets,
    // then any remaining structure sets (with parent info as subtitle), then doses.
    const flatItems = React.useMemo<FlatItem[]>(() => {
        const items: FlatItem[] = [];
        const placed = new Set<string>();

        for (const image of exportableImages) {
            items.push({ type: 'image', item: image });
            for (const ss of exportableStructureSets) {
                if (ss.image?.dicomUid === image.dicomUid) {
                    items.push({
                        type: 'structureset',
                        item: ss,
                        subtitle: image.seriesMetadata.seriesDescription,
                    });
                    placed.add(ss.dicomUid);
                }
            }
        }

        // Remaining SSs: orphans (no parent) get no subtitle;
        // SSs with a non-exportable parent get "description (modality)" as subtitle.
        for (const ss of exportableStructureSets) {
            if (!placed.has(ss.dicomUid)) {
                const parent = ss.image;
                items.push({
                    type: 'structureset',
                    item: ss,
                    subtitle: parent
                        ? `${parent.seriesMetadata.seriesDescription} (${parent.seriesMetadata.modality})`
                        : undefined,
                });
            }
        }

        for (const dose of exportableDoses) {
            items.push({ type: 'dose', item: dose });
        }

        return items;
    }, [exportableImages, exportableStructureSets, exportableDoses]);

    return (
        <Box
            sx={(theme) => ({
                borderBottom: `1px solid ${theme.palette.divider}`,
            })}
        >
            {hasExportableItems && (
                <Box display="flex" alignItems="center" px={1} py={0.5}>
                    <Checkbox
                        data-testid="exportSelectAllCheckbox"
                        size="small"
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        inputProps={{
                            'aria-label': 'Select all exportable items',
                        }}
                    />
                    <Typography variant="body2" fontSize={13}>
                        Select all ({totalSelectedCount}/{totalExportableCount})
                    </Typography>
                </Box>
            )}

            {!hasExportableItems && (
                <Box
                    width="100%"
                    display="flex"
                    alignItems="center"
                    p={2}
                    sx={(theme) => ({
                        borderRadius: `${theme.shape.borderRadius}px`,
                        border: `1px solid ${theme.palette.info.main}`,
                        backgroundColor: alpha(theme.palette.info.main, 0.1),
                        color: theme.palette.info.main,
                    })}
                >
                    <Typography variant="body2" color="primary">
                        There is no generated datasets to export.
                    </Typography>
                </Box>
            )}

            {flatItems.map((entry) => {
                if (entry.type === 'image') {
                    const image = entry.item;
                    return (
                        <ExportListItem
                            key={image.dicomUid}
                            label={image.seriesMetadata.seriesDescription}
                            modality={image.seriesMetadata.modality}
                            isDerived={image.isNew}
                            expanded={expanded === image.dicomUid}
                            onChange={handleExpand(image.dicomUid)}
                            isSelected={selectedImages.includes(image)}
                            onSelect={() => handleSelectImage(image)}
                            isExported={image.isExported}
                            details={
                                <ImageExportDetails
                                    image={image}
                                    edits={imageEdits[image.dicomUid]}
                                    onSeriesDescriptionChange={(desc) =>
                                        setImageSeriesDescription(
                                            image.dicomUid,
                                            desc
                                        )
                                    }
                                    onSeriesNumberChange={(num) =>
                                        setImageSeriesNumber(
                                            image.dicomUid,
                                            num
                                        )
                                    }
                                />
                            }
                        />
                    );
                }

                if (entry.type === 'structureset') {
                    const ss = entry.item;
                    return (
                        <ExportListItem
                            key={ss.dicomUid}
                            label={
                                ssEdits[ss.dicomUid]?.label ??
                                ss.structureSetLabel
                            }
                            modality={Modality.RTSTRUCT}
                            isDerived={ss.isNew}
                            expanded={expanded === ss.dicomUid}
                            onChange={handleExpand(ss.dicomUid)}
                            isSelected={selectedStructureSets.includes(ss)}
                            onSelect={() => handleSelectStructureSet(ss)}
                            isExported={ss.isExported}
                            subtitle={entry.subtitle}
                            details={
                                <StructureSetExportDetails
                                    structureSet={ss}
                                    selectedStructures={
                                        structureSelections[ss.dicomUid] || []
                                    }
                                    onSelectStructure={(structure) =>
                                        handleSelectStructure(
                                            ss.dicomUid,
                                            structure
                                        )
                                    }
                                    ssEdits={ssEdits}
                                    roiEdits={roiEdits}
                                    setSsLabel={setSsLabel}
                                    setSsApproval={setSsApproval}
                                    setSsReviewer={setSsReviewer}
                                    setRoiName={setRoiName}
                                    setRoiType={setRoiType}
                                />
                            }
                        />
                    );
                }

                if (entry.type === 'dose') {
                    const dose = entry.item;
                    const hasObjectives =
                        (dose.optimizationObjectives?.length ?? 0) > 0;
                    return (
                        <ExportListItem
                            key={dose.dicomUid}
                            label={dose.plan?.label ?? 'Dose'}
                            modality={Modality.RTDOSE}
                            isDerived={dose.isNew}
                            expanded={expanded === dose.dicomUid}
                            onChange={handleExpand(dose.dicomUid)}
                            isSelected={selectedDoses.includes(dose)}
                            onSelect={() => handleSelectDose(dose)}
                            isExported={dose.isExported}
                            details={<DoseExportDetails dose={dose} />}
                            extraBadge={
                                hasObjectives ? (
                                    <Tooltip title="Optimization objectives (JSON + CSV) will be included in the download">
                                        <Chip
                                            label="+ Optimization Objectives"
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                height: 18,
                                                fontSize: 10,
                                                '& .MuiChip-label': {
                                                    px: 0.75,
                                                },
                                            }}
                                        />
                                    </Tooltip>
                                ) : undefined
                            }
                        />
                    );
                }

                return null;
            })}

            {exportableRegistrations?.map((registration) => {
                const label =
                    registration.contentLabel ||
                    registration.contentDescription ||
                    registration.dicomUid;
                return (
                    <ExportListItem
                        key={registration.dicomUid}
                        label={label}
                        modality={Modality.REG}
                        isDerived={registration.isNew}
                        expanded={expanded === registration.dicomUid}
                        onChange={handleExpand(registration.dicomUid)}
                        isSelected={selectedRegistrations.includes(
                            registration
                        )}
                        onSelect={() => handleSelectRegistration(registration)}
                        isExported={registration.isExported}
                        details={
                            <Typography variant="body2" color="text.secondary">
                                {registration.sourceFrameOfReferenceUid}
                                {' → '}
                                {registration.targetFrameOfReferenceUid}
                            </Typography>
                        }
                    />
                );
            })}
        </Box>
    );
};

export default ExportDatasetModal;
